import installCsrfInterceptor from './installCsrfInterceptor'

/**
 * Register the CSRF client before anything else in a pack can issue a write.
 *
 * PLT-2385 — PLT-2324 shipped this client and a production `CSRF_MODE=log` window
 * then measured ten controllers that never once carried a token across 18 hours and
 * dozens of accounts: `item_attributes` alone was 0 of 10,650. The discriminator was
 * not the layout, the view, the pack or the deployed bundle — the same user on the
 * same controller flipped between bursts minutes apart, and every burst was uniformly
 * tokenless or uniformly tokenful. The only variable left is elapsed time since page
 * load, i.e. whether the registration had run yet when the page's first write went out.
 *
 * WHY A MODULE AND NOT ANOTHER CALL IN A PACK. Registration used to live in exactly
 * two places — `packs/header_components.js` and `AxiosService.js` — and both are
 * reachable only if something on the page happens to evaluate them first. That is a
 * webpack emission detail and a view-authoring habit, not a contract, and both have
 * already been broken in ways nothing failed on:
 *
 *   - `app/views/inspections/edit.haml` emits its pack with `defer: false`. Classic
 *     scripts run during parsing, before ANY `defer` script, so `inspection_components`
 *     and every chunk it names execute ahead of `header_components.js` even though the
 *     markup lists that first.
 *   - `spectora_admin/layouts/admin.html.erb` and `inspections/reschedule_modal_iframe.haml`
 *     render no `_shared/header_scripts` at all, so `header_components` never loads there.
 *
 * `config/webpack/webpack.config.js` — the one config a build loads — therefore prepends
 * THIS module to every JavaScript entry, so it is the first module of every pack's own
 * graph. Whichever entry chunk the browser reaches first performs the registration, and
 * there is no "first" left to get wrong. It also
 * survives module duplication, which a single privileged install site cannot: the
 * bootstrap resolves the same `axios` module the rest of its own entry resolves, and
 * `utils/csrfToken.js` is already known to be emitted into more than one chunk.
 *
 * IDEMPOTENT BY CONSTRUCTION. `installCsrfInterceptor` keys a `WeakSet` on the instance,
 * so the pack-level calls in `header_components.js`, `AxiosService.js` and
 * `packs/spectora_admin.js` become no-op second calls rather than a second interceptor
 * stacking a duplicate header. They are deliberately left in place: they are what covers
 * a consumer that builds its own instance, which this module cannot see.
 *
 * WHAT THIS DOES NOT DO. It does not widen the origin gate: `installCsrfInterceptor`
 * still stamps only same-origin writes and still fails closed on an unparseable URL, so
 * a pack rendered on a third-party origin gains the registration and attaches nothing.
 * And it does not reach an `axios.create()` instance — those inherit config but not
 * interceptors, and each still has to call `installCsrfInterceptor(instance)` itself.
 *
 * It also does NOT seed `window.__spectoraCsrfToken` from the rendered meta tag. Every
 * reader falls back to the tag on its own when the slot is empty, and an empty slot is
 * the fresher answer: it re-reads the LIVE tag per call, where a seeded one would pin a
 * render-time snapshot for the life of the tab. `csrfToken.js` carries the full reading.
 */
installCsrfInterceptor()
